import React from 'react'

/**
 * RELEASE LIST LOADING SKELETON
 *
 * Shimmer-animated placeholder matching the ReleaseList page layout.
 * Used while release data is loading on the /releases page.
 *
 * Structure mirrors the real page:
 * - Page header (breadcrumb + toggle + "New Release" button)
 * - Blue-tinted content area with 5 skeleton release cards
 * - Each card mimics ReleaseCard: name, status lozenge, stats row, progress bar
 *
 * Reuses existing .skeleton-bone class and skeletonShimmer animation from index.css.
 */
export function ReleaseListSkeleton() {
  // Varying name widths for visual naturalness across the 5 cards
  const cardNameWidths = ['60%', '45%', '70%', '50%', '55%']
  // Some cards show description, some don't (mimics real data)
  const showDescription = [true, false, true, true, false]

  return (
    <div className="release-list-page">
      {/* Header Skeleton - breadcrumb + toggle + button */}
      <div className="release-page-header">
        {/* Breadcrumb placeholder: Project / Releases */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div className="skeleton-bone" style={{ width: '60px', height: '14px' }} />
          <span style={{ color: 'var(--ds-text-subtlest, #A5ADBA)', fontSize: '14px' }}>/</span>
          <div className="skeleton-bone" style={{ width: '60px', height: '14px' }} />
        </div>

        {/* Right side: toggle + button placeholders */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* "Show Archived" toggle placeholder */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="skeleton-bone" style={{ width: '90px', height: '12px' }} />
            <div className="skeleton-bone" style={{ width: '36px', height: '20px', borderRadius: '10px' }} />
          </div>
          {/* "New Release" button placeholder */}
          <div className="skeleton-bone" style={{ width: '120px', height: '32px', borderRadius: '3px' }} />
        </div>
      </div>

      {/* Content Area - blue-tinted background with skeleton cards */}
      <div className="release-list-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {cardNameWidths.map((nameWidth, index) => (
            <div key={index} className="rl-skeleton-card">
              {/* Row 1: Ship icon + Release name + Status lozenge + More button */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  {/* Ship icon placeholder */}
                  <div className="skeleton-bone skeleton-circle" style={{ width: '20px', height: '20px' }} />
                  {/* Release name placeholder - varying widths */}
                  <div className="skeleton-bone" style={{ width: nameWidth, maxWidth: '280px', height: '16px' }} />
                  {/* Status lozenge placeholder */}
                  <div className="skeleton-bone" style={{ width: '60px', height: '20px', borderRadius: '3px' }} />
                </div>
                {/* More actions button placeholder */}
                <div className="skeleton-bone" style={{ width: '32px', height: '32px', borderRadius: '3px' }} />
              </div>

              {/* Row 2: Description (shown on some cards for variety) */}
              {showDescription[index] && (
                <div className="skeleton-bone" style={{ width: '80%', height: '12px' }} />
              )}

              {/* Row 3: Stats row - date, folders, scenarios, runs */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Date stat */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div className="skeleton-bone skeleton-circle" style={{ width: '14px', height: '14px' }} />
                  <div className="skeleton-bone" style={{ width: '70px', height: '11px' }} />
                </div>
                {/* Folder stat */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '12px', borderLeft: '1px solid var(--ds-border, #DFE1E6)' }}>
                  <div className="skeleton-bone skeleton-circle" style={{ width: '14px', height: '14px' }} />
                  <div className="skeleton-bone" style={{ width: '55px', height: '11px' }} />
                </div>
                {/* Scenario stat */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '12px', borderLeft: '1px solid var(--ds-border, #DFE1E6)' }}>
                  <div className="skeleton-bone skeleton-circle" style={{ width: '14px', height: '14px' }} />
                  <div className="skeleton-bone" style={{ width: '65px', height: '11px' }} />
                </div>
                {/* Run stat */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '12px', borderLeft: '1px solid var(--ds-border, #DFE1E6)' }}>
                  <div className="skeleton-bone skeleton-circle" style={{ width: '14px', height: '14px' }} />
                  <div className="skeleton-bone" style={{ width: '40px', height: '11px' }} />
                </div>
              </div>

              {/* Row 4: Progress bar + percentage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Progress bar placeholder */}
                <div className="skeleton-bone" style={{ flex: 1, height: '6px', borderRadius: '3px' }} />
                {/* Percentage placeholder */}
                <div className="skeleton-bone" style={{ width: '45px', height: '13px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
